/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import type { UniversalNavLinkType } from '../../../../constants';
import { SubMenuHeading } from './SubMenuHeading';
import { SubMenuList } from './SubMenuList';
import { ICON_SIZE, HEADING_GAP } from './constants';

type Props = {
  /** Top-level headings along with an optional description or list of links */
  links: UniversalNavLinkType[];
  /** Name of the parent nav item to be included in tracking event payloads */
  parent?: string;
  /** Function called when closing menu on a link from within the MegaMenu */
  onCloseMenu?: () => void;
};

const Styles = labelStyles('MegaMenuSubmenu', {
  root: css({
    color: 'var(--zds-text-stronger, #201515)',
    display: 'grid',
    gap: '7%',
    gridTemplateColumns: 'repeat(3, minmax(0px, max-content))',
    gridTemplateRows: 'auto',
    height: '100%',
  }),
  groupContainer: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '35px',
  }),
  group: css({
    color: 'inherit',
    '&:hover, &:focus, &:focus-within': {
      color: 'var(--zds-brand-orange, #FF4F00)',
    },
  }),

  listWrapper: (hasIcon: boolean) =>
    css({
      paddingLeft: hasIcon ? `${ICON_SIZE + HEADING_GAP}px` : 0,
    }),
});

/**
 * @private
 * Group of links inside the MegaMenu
 */
export const MegaMenuSubmenu = (props: Props) => {
  return (
    <ul css={Styles.root} data-zds>
      {props.links.map((link) => (
        <li key={link.text} css={Styles.group} data-zds>
          <div role={link.submenu ? 'group' : 'presentation'} data-zds>
            <SubMenuHeading link={link} onCloseMenu={props.onCloseMenu} />
            {link.submenu && (
              <div css={Styles.listWrapper(!!link.icon)} data-zds>
                <SubMenuList
                  submenu={link.submenu}
                  parent={props.parent}
                  onCloseMenu={props.onCloseMenu}
                />
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};
